








import DashboardUtils, {
  DashboardTile,
  TileMultipleChoiceFrequencyOptions,
} from '@/utils/DashboardUtils';
import { OversightOrganization } from '@/utils/OversightUtils';
import { SurveyGroup } from '@/utils/SurveyUtils';
import { HorizontalBar, Bar } from 'vue-chartjs';
import { rgbToHex } from '@/utils/ChartUtils';
import mixins from 'vue-typed-mixins';
import { TileAPIParams } from '@/utils/TileUtils';

const Chart = mixins().extend({
  props: {
    tile: {
      type: Object as () => DashboardTile<TileMultipleChoiceFrequencyOptions>,
    },
    data: {
      type: Array as () => {
        questionkey: string;
        frequency?: {
          choicekey: string;
          frequency: number;
        }[];
      }[],
    },
  },

  computed: {
    chartData(): any {
      if (
        this.tile.options.style === 'stack' ||
        this.tile.options.style === 'group'
      ) {
        return {
          labels: this.tile.options.answers?.map((answer) =>
            this.$translate(answer.label)
          ),
          datasets: this.tile.options.questionkeys.map((questionkey) => {
            const data = this.tile.options.answers?.map((answer) => 0);
            this.data?.forEach((question) => {
              if (question.questionkey === questionkey.key) {
                question.frequency?.forEach((answer) => {
                  const index = this.tile.options.answers?.findIndex((axes) =>
                    answer.choicekey
                      ? axes.keys.includes(answer.choicekey)
                      : false
                  );
                  if (index >= 0) {
                    data[index] += answer.frequency;
                  }
                });
              }
            });

            return {
              label: this.$translate(questionkey.label),
              backgroundColor: rgbToHex(questionkey.color as any),
              data: data,
            };
          }),
        };
      } else {
        const data = this.tile.options.answers?.map((answer) => 0);
        this.data?.forEach((question) => {
          question.frequency?.forEach((answer) => {
            const index = this.tile.options.answers?.findIndex((axes) =>
              answer.choicekey ? axes.keys.includes(answer.choicekey) : false
            );
            if (index >= 0) {
              data[index] += answer.frequency;
            }
          });
        });

        return {
          labels: this.tile.options.answers?.map((answer) =>
            this.$translate(answer.label)
          ),
          datasets: [
            {
              backgroundColor: this.tile.options.answers?.map((answer) =>
                rgbToHex(answer.color as any)
              ),
              data: data,
            },
          ],
        };
      }
    },

    chartOptions(): any {
      return {
        title: {
          display: false,
        },
        legend: {
          display:
            this.tile.options.style === 'stack' ||
            this.tile.options.style === 'group',
        },
        scales: {
          xAxes: [
            {
              scaleLabel: {
                display: !!this.$translate(this.tile.options?.xaxes),
                labelString: this.$translate(this.tile.options.xaxes),
              },
              stacked: this.tile.options.style === 'stack',
              ticks: {
                stepSize: 1,
                beginAtZero: true,
              },
            },
          ],
          yAxes: [
            {
              scaleLabel: {
                display: !!this.$translate(this.tile.options?.yaxes),
                labelString: this.$translate(this.tile.options.yaxes),
              },
              stacked: this.tile.options.style === 'stack',
              ticks: {
                stepSize: 1,
                beginAtZero: true,
              },
            },
          ],
        },
        responsive: true,
        maintainAspectRatio: false,
      };
    },
  },

  mounted() {
    // @ts-ignore
    this.renderChart(this.chartData, this.chartOptions);
  },

  watch: {
    chartData() {
      this.reload();
    },
    chartOptions() {
      this.reload();
    },
  },

  methods: {
    reload() {
      this.$data._chart.data = this.chartData;
      this.$data._chart.options = this.chartOptions;
      this.$data._chart.update();
    },
  },
});

const VerticalChart = mixins(Chart).extend({
  extends: Bar,
});

const HorizontalChart = mixins(Chart).extend({
  extends: HorizontalBar,
});

export default mixins().extend({
  components: {
    VerticalChart,
    HorizontalChart,
  },

  props: {
    tile: {
      type: Object as () => DashboardTile<TileMultipleChoiceFrequencyOptions>,
    },

    params: {
      type: Object as () => TileAPIParams,
    },
  },

  data(): {
    data?: {
      questionkey: string;
      frequency?: {
        choicekey: string;
        frequency: number;
      }[];
    }[];
  } {
    return {
      data: undefined,
    };
  },

  mounted() {
    this.load();
  },

  watch: {
    params(current, previous) {
      if (
        current.surveygroup !== previous.surveygroup ||
        current.oversight !== previous.oversight
      ) {
        this.load();
      }
    },
  },

  methods: {
    async load() {
      if (this.params?.surveygroup?.id) {
        try {
          this.data = undefined;
          this.$alert.hide('load');
          this.data = await DashboardUtils.getMultipleChoiceFrequency({
            surveygroup: this.params.surveygroup,
            oversight: this.params.oversight,
            questionkeys: this.tile.options?.questionkeys?.map(
              (questionkey) => questionkey.key
            ),
          });
        } catch (error) {
          this.$alert.show('load', {
            type: 'error',
            messages: this.$handleAPIError(error),
          });
        }
      }
    },
  },
});
